import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import { IARDashboardDataSliceState } from "../../utils/ARDashboard.types";
import { fetchAPDashboardAging, fetchAPDashboardCustomersDelay, fetchAPDashboardCustomersDue, fetchAPDashboardCustomersListDue, fetchAPDashboardDSOTrend, fetchAPDashboardInvoices, fetchAPDashboardNumbers, fetchAPDashboardSales, fetchAPDashboardListCustomersDelay, fetchAPDashboardExpenses } from "./APDashboardThunk";

/**
 * AP Dashboard slice initial state.
 */
const initialState: IARDashboardDataSliceState = {
    numbers: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardNumbers: {
            totalAP: 0,
            totalAPDSO: 0,
            totalAR: 0,
            totalARDSO: 0,
            totalCustomers: 0,
            totalVendors: 0
        }
    },
    sales: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardSales: {
            series: null,
            salesValue: 0,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    expenses: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardExpenses: {
            series: null,
            expenseValue: 0
        }
    },
    invoices: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardInvoices: {
            series: null,
            invoiceValue: 0,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    aging: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardAging: {
            agingValue: 0,
            series: null,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    DSOTrend: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardDSOTrend: {
            DSOTrendValue: 0,
            series: null,
            trend: {
                hasGrown: false,
                value: 0
            }
        }
    },
    customerDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerDelay: {
            list: []
        },
    },
    customerDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerDue: {
            list: []
        }
    },
    customerARListDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDue: {
            list: []
        }
    },
    customerAPListDue: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDue: {
            list: []
        }
    },
    customerARListDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDelay: {
            list: []
        }
    },
    customerAPListDelay: {
        errorMessage: '',
        status: ApiStatus.IDLE,
        dashboardCustomerListDelay: {
            list: []
        }
    }
}

/**
 * AP Dashboard Slice.
 */
const APDashboardSlice = createSlice({
    name: 'APDashboard',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch AP Dashboard Numbers
            .addCase(fetchAPDashboardNumbers.pending, (state, action) => {
                state.numbers.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardNumbers.fulfilled, (state, action) => {
                state.numbers.status = ApiStatus.SUCCESS;
                state.numbers.dashboardNumbers = action.payload || [];
                state.numbers.errorMessage = '';
            })
            .addCase(fetchAPDashboardNumbers.rejected, (state, action) => {
                state.numbers.status = ApiStatus.FAILED;
                state.numbers.errorMessage = action.error.message
            })


            // Fetch AP Dashboard Sales
            .addCase(fetchAPDashboardSales.pending, (state, action) => {
                state.sales.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardSales.fulfilled, (state, action) => {
                state.sales.status = ApiStatus.SUCCESS;
                state.sales.dashboardSales = action.payload || [];
                state.sales.errorMessage = '';
            })
            .addCase(fetchAPDashboardSales.rejected, (state, action) => {
                state.sales.status = ApiStatus.FAILED;
                state.sales.errorMessage = action.error.message
            })

            // Fetch AP Dashboard Expenses
            .addCase(fetchAPDashboardExpenses.pending, (state, action) => {
                state.expenses!.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardExpenses.fulfilled, (state, action) => {
                state.expenses!.status = ApiStatus.SUCCESS;
                state.expenses!.dashboardExpenses = action.payload;
                state.expenses!.errorMessage = '';
            })
            .addCase(fetchAPDashboardExpenses.rejected, (state, action) => {
                state.expenses!.status = ApiStatus.FAILED;
                state.expenses!.errorMessage = action.error.message
            })

            // Fetch AP Dashboard Invoices
            .addCase(fetchAPDashboardInvoices.pending, (state, action) => {
                state.invoices.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardInvoices.fulfilled, (state, action) => {
                state.invoices.status = ApiStatus.SUCCESS;
                state.invoices.dashboardInvoices = action.payload || [];
                state.invoices.errorMessage = '';
            })
            .addCase(fetchAPDashboardInvoices.rejected, (state, action) => {
                state.invoices.status = ApiStatus.FAILED;
                state.invoices.errorMessage = action.error.message
            })

            // Fetch AP Dashboard Aging
            .addCase(fetchAPDashboardAging.pending, (state, action) => {
                state.aging.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardAging.fulfilled, (state, action) => {
                state.aging.status = ApiStatus.SUCCESS;
                state.aging.dashboardAging = action.payload || [];
                state.aging.errorMessage = '';
            })
            .addCase(fetchAPDashboardAging.rejected, (state, action) => {
                state.aging.status = ApiStatus.FAILED;
                state.aging.errorMessage = action.error.message
            })

            // Fetch AP Dashboard DSO-Trend
            .addCase(fetchAPDashboardDSOTrend.pending, (state, action) => {
                state.DSOTrend.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardDSOTrend.fulfilled, (state, action) => {
                state.DSOTrend.status = ApiStatus.SUCCESS;
                state.DSOTrend.dashboardDSOTrend = action.payload || [];
                state.DSOTrend.errorMessage = '';
            })
            .addCase(fetchAPDashboardDSOTrend.rejected, (state, action) => {
                state.DSOTrend.status = ApiStatus.FAILED;
                state.DSOTrend.errorMessage = action.error.message
            })

            // Fetch AP Dashboard Due
            .addCase(fetchAPDashboardCustomersDue.pending, (state, action) => {
                state.customerDue.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardCustomersDue.fulfilled, (state, action) => {
                state.customerDue.status = ApiStatus.SUCCESS;
                state.customerDue.dashboardCustomerDue = action.payload || [];
                state.customerDue.errorMessage = '';
            })
            .addCase(fetchAPDashboardCustomersDue.rejected, (state, action) => {
                state.customerDue.status = ApiStatus.FAILED;
                state.customerDue.errorMessage = action.error.message
            })

            // Fetch AP Dashboard customer list Due
            .addCase(fetchAPDashboardCustomersListDue.pending, (state, action) => {
                state.customerAPListDue.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardCustomersListDue.fulfilled, (state, action) => {
                state.customerAPListDue.status = ApiStatus.SUCCESS;
                state.customerAPListDue.dashboardCustomerListDue = action.payload || [];
                state.customerAPListDue.errorMessage = '';
            })
            .addCase(fetchAPDashboardCustomersListDue.rejected, (state, action) => {
                state.customerAPListDue.status = ApiStatus.FAILED;
                state.customerAPListDue.errorMessage = action.error.message
            })

            // Fetch AP Dashboard Delay
            .addCase(fetchAPDashboardCustomersDelay.pending, (state, action) => {
                state.customerDelay.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardCustomersDelay.fulfilled, (state, action) => {
                state.customerDelay.status = ApiStatus.SUCCESS;
                state.customerDelay.dashboardCustomerDelay = action.payload || [];
                state.customerDelay.errorMessage = '';
            })
            .addCase(fetchAPDashboardCustomersDelay.rejected, (state, action) => {
                state.customerDelay.status = ApiStatus.FAILED;
                state.customerDelay.errorMessage = action.error.message
            })

            // Fetch AP Dashboard customer list Delay
            .addCase(fetchAPDashboardListCustomersDelay.pending, (state, action) => {
                state.customerAPListDelay.status = ApiStatus.LOADING;
            })
            .addCase(fetchAPDashboardListCustomersDelay.fulfilled, (state, action) => {
                state.customerAPListDelay.status = ApiStatus.SUCCESS;
                state.customerAPListDelay.dashboardCustomerListDelay = action.payload || [];
                state.customerAPListDelay.errorMessage = '';
            })
            .addCase(fetchAPDashboardListCustomersDelay.rejected, (state, action) => {
                state.customerAPListDelay.status = ApiStatus.FAILED;
                state.customerAPListDelay.errorMessage = action.error.message
            })
    }
});

export default APDashboardSlice.reducer;
